import { useState } from "react";
import BodhiBrowseTile from "../../Cards/BodhiBrowseTile";
import "./BodhiPlanner.css";

const DEFAULT_ITEMS = [
	{
		time: "07:00",
		label: "Morning Yoga",
		sub: "Sunrise Flow · Studio A",
		color: "#a3c4a0",
		fill: 30,
	},
	{
		time: "11:00",
		label: "Spa Treatments",
		sub: "Deep Calm · Rooms 1–4",
		color: "#88c9bf",
		fill: 70,
	},
	{
		time: "15:00",
		label: "Afternoon Lounge",
		sub: "Slow Textures · Relaxation",
		color: "#b8a8d4",
		fill: 50,
	},
	{
		time: "19:00",
		label: "Evening Wind-down",
		sub: "Ambient 432 Hz · Suites",
		color: "#e0b088",
		fill: 90,
	},
];

const EditIcon = () => (
	<svg
		width="14"
		height="14"
		viewBox="0 0 16 16"
		fill="none"
		aria-hidden="true"
	>
		<title>Edit</title>
		<path
			d="M11.5 2.5l2 2L6 12l-2.5.5L4 10z"
			stroke="currentColor"
			strokeWidth="1.3"
			strokeLinejoin="round"
		/>
	</svg>
);

const TrashIcon = () => (
	<svg
		width="14"
		height="14"
		viewBox="0 0 16 16"
		fill="none"
		aria-hidden="true"
	>
		<title>Delete</title>
		<path
			d="M3 4.5h10M6.5 4V2.8h3V4M4.5 4.5l.5 8.5h6l.5-8.5"
			stroke="currentColor"
			strokeWidth="1.3"
			strokeLinecap="round"
			strokeLinejoin="round"
		/>
	</svg>
);

const CheckIcon = () => (
	<svg
		width="14"
		height="14"
		viewBox="0 0 16 16"
		fill="none"
		aria-hidden="true"
	>
		<title>Confirm</title>
		<path
			d="M3.5 8.5l3 3 6-7"
			stroke="currentColor"
			strokeWidth="1.6"
			strokeLinecap="round"
			strokeLinejoin="round"
		/>
	</svg>
);

const CloseIcon = () => (
	<svg
		width="14"
		height="14"
		viewBox="0 0 16 16"
		fill="none"
		aria-hidden="true"
	>
		<title>Cancel</title>
		<path
			d="M4 4l8 8M12 4l-8 8"
			stroke="currentColor"
			strokeWidth="1.5"
			strokeLinecap="round"
		/>
	</svg>
);

const ClockIcon = () => (
	<svg
		width="11"
		height="11"
		viewBox="0 0 12 12"
		fill="none"
		aria-hidden="true"
	>
		<title>Schedule</title>
		<circle cx="6" cy="6" r="4.5" stroke="currentColor" strokeWidth="1.1" />
		<path
			d="M6 3.6V6l1.6 1"
			stroke="currentColor"
			strokeWidth="1.1"
			strokeLinecap="round"
			strokeLinejoin="round"
		/>
	</svg>
);

const itemKey = (item) => item.id ?? `${item.time}-${item.label}`;

// Small enough that a handful of targets still fit on one line.
const TARGET_TILE_SIZE = 72;
const MAX_TARGET_TILES = 6;

/**
 * The artwork of what a slot plays: the same typed tiles the browse shelves
 * use, shrunk down and scrolled sideways when a slot has many targets.
 */
const SlotTargets = ({ targets, onTargetClick }) => {
	const shown = targets.slice(0, MAX_TARGET_TILES);
	const overflow = targets.length - shown.length;

	return (
		<div className="bodhi-planner-slot-targets">
			{shown.map((target) => (
				<BodhiBrowseTile
					key={`${target.type}-${target.id}`}
					className="bodhi-planner-target"
					type={target.type}
					name={target.name}
					cover={target.cover}
					covers={target.covers}
					playlist={target.type === "playlist" ? target : null}
					size={TARGET_TILE_SIZE}
					onClick={onTargetClick ? () => onTargetClick(target) : undefined}
				/>
			))}
			{overflow > 0 && (
				<span className="bodhi-planner-target-more">+{overflow}</span>
			)}
		</div>
	);
};

const BodhiPlanner = ({
	title = "BodhiStreams Planner",
	date = "Today",
	items = DEFAULT_ITEMS,
	onEdit,
	onDelete,
	onToggleActive,
	onTargetClick,
	pendingToggleKey = null,
	className = "",
}) => {
	const [confirmingKey, setConfirmingKey] = useState(null);
	const cls = ["bodhi-planner", className].filter(Boolean).join(" ");

	return (
		<div className={cls}>
			<div className="bodhi-planner-header">
				<span className="bodhi-planner-dot is-r" />
				<span className="bodhi-planner-dot is-y" />
				<span className="bodhi-planner-dot is-g" />
				<span className="bodhi-planner-title">
					{title}
					{date ? ` · ${date}` : ""}
				</span>
			</div>
			<div className="bodhi-planner-day">
				{items.map((item) => {
					const key = itemKey(item);
					const confirming = confirmingKey === key;
					const hasToggle =
						item.active !== undefined && typeof onToggleActive === "function";
					const hasActions = hasToggle || onEdit || onDelete;

					return (
						<div
							key={key}
							className="bodhi-planner-slot"
							style={{ "--slot-color": item.color }}
						>
							<span className="bodhi-planner-slot-time">{item.time}</span>
							<div className="bodhi-planner-slot-body">
								<div className="bodhi-planner-slot-head">
									<span className="bodhi-planner-slot-label">{item.label}</span>
									{item.type && (
										<span className="bodhi-planner-slot-type">{item.type}</span>
									)}
								</div>
								{item.sub && (
									<span className="bodhi-planner-slot-sub">{item.sub}</span>
								)}
								{item.schedule && (
									<span className="bodhi-planner-slot-schedule">
										<ClockIcon />
										{item.schedule}
										{item.zone && (
											<span className="bodhi-planner-slot-zone">
												{item.zone}
											</span>
										)}
									</span>
								)}
								{item.targets?.length > 0 && (
									<SlotTargets
										targets={item.targets}
										onTargetClick={onTargetClick}
									/>
								)}
								{item.fill !== undefined && item.fill !== null && (
									<div className="bodhi-planner-slot-bar">
										<div
											className="bodhi-planner-slot-bar-fill"
											style={{ width: `${item.fill}%` }}
										/>
									</div>
								)}
							</div>

							{hasActions && (
								<div className="bodhi-planner-slot-actions">
									{confirming ? (
										<>
											<button
												type="button"
												className="bodhi-planner-action is-confirm"
												aria-label="Confirm delete"
												onClick={() => {
													onDelete?.(item);
													setConfirmingKey(null);
												}}
											>
												<CheckIcon />
											</button>
											<button
												type="button"
												className="bodhi-planner-action"
												aria-label="Cancel"
												onClick={() => setConfirmingKey(null)}
											>
												<CloseIcon />
											</button>
										</>
									) : (
										<>
											{hasToggle && (
												<button
													type="button"
													className={`bodhi-planner-toggle ${
														item.active ? "is-on" : ""
													} ${pendingToggleKey === key ? "is-pending" : ""}`}
													role="switch"
													aria-checked={item.active}
													aria-busy={pendingToggleKey === key || undefined}
													aria-label={item.active ? "Deactivate" : "Activate"}
													disabled={pendingToggleKey === key}
													onClick={() => onToggleActive(item, !item.active)}
												/>
											)}
											{onEdit && (
												<button
													type="button"
													className="bodhi-planner-action"
													aria-label="Edit"
													onClick={() => onEdit(item)}
												>
													<EditIcon />
												</button>
											)}
											{onDelete && (
												<button
													type="button"
													className="bodhi-planner-action is-danger"
													aria-label="Delete"
													onClick={() => setConfirmingKey(key)}
												>
													<TrashIcon />
												</button>
											)}
										</>
									)}
								</div>
							)}
						</div>
					);
				})}
			</div>
		</div>
	);
};

export default BodhiPlanner;
